import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AdminNav from "@/components/AdminNav";
import "@/styles/portfolio.css";
import "@/styles/admin.css";

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("de-AT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return s;
  }
}

export default function AdminDeliveries() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [copiedId, setCopiedId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/deliveries");
      setItems(data);
      setError("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter(
      (it) =>
        it.client_name.toLowerCase().includes(term) ||
        it.slug.toLowerCase().includes(term)
    );
  }, [items, q]);

  const copyLink = async (slug, id) => {
    const url = `${window.location.origin}/drop/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(""), 1500);
    } catch {
      window.prompt("Kopieren:", url);
    }
  };

  const toggleActive = async (it) => {
    try {
      await api.post(`/admin/deliveries/${it.id}/toggle-active`);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const remove = async (it) => {
    if (
      !window.confirm(
        `Übergabe "${it.client_name}" wirklich löschen?\nAlle Videos werden aus dem Cloud-Speicher entfernt.`
      )
    )
      return;
    try {
      await api.delete(`/admin/deliveries/${it.id}`);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="admin-shell" data-testid="admin-deliveries">
      <header className="admin-header">
        <div>
          <span className="eyebrow">ADMIN · ÜBERGABEN</span>
          <h1 className="display admin-h1">
            Kunden-<span className="italic">Übergaben</span>.
          </h1>
          <p className="admin-subtitle">Private Video-Links mit optionalem Passwortschutz</p>
        </div>
        <div className="admin-header__actions">
          <Link to="/admin/deliveries/new" className="btn btn--solid" data-testid="delivery-new">
            <span>Neue Übergabe</span>
            <span className="btn__arrow">→</span>
          </Link>
          <button type="button" onClick={handleLogout} className="btn btn--ghost">
            <span>Abmelden</span>
          </button>
        </div>
      </header>

      <AdminNav active="/admin/deliveries" />

      {error && <div className="admin-error">{error}</div>}

      <div className="delivery-toolbar">
        <input
          type="search"
          className="admin-input"
          placeholder="Suche nach Kunde oder Slug …"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="delivery-search"
        />
      </div>

      {loading && <p className="admin-empty">Lädt…</p>}
      {!loading && filtered.length === 0 && (
        <p className="admin-empty">
          Noch keine Übergaben — klick auf &bdquo;Neue Übergabe&ldquo;.
        </p>
      )}

      <ul className="delivery-grid" data-testid="delivery-list">
        {filtered.map((it) => {
          const link = `/drop/${it.slug}`;
          const statusTag = !it.active
            ? { label: "Deaktiviert", cls: "tag tag--off" }
            : it.expired
            ? { label: "Abgelaufen", cls: "tag tag--off" }
            : { label: "Aktiv", cls: "tag tag--on" };
          return (
            <li key={it.id} className="delivery-card" data-testid={`delivery-${it.id}`}>
              <div className="delivery-card__head">
                <span className={statusTag.cls}>{statusTag.label}</span>
                {it.password_protected && (
                  <span className="tag tag--lock" title="Passwortgeschützt">🔒</span>
                )}
              </div>
              <h2 className="delivery-card__title">{it.client_name}</h2>
              <span className="delivery-card__slug">/drop/{it.slug}</span>
              <div className="delivery-card__meta">
                <span>{it.video_count} {it.video_count === 1 ? "Video" : "Videos"}</span>
                <span>· {it.view_count} Aufrufe</span>
                <span>· {it.download_count} Downloads</span>
              </div>
              <div className="delivery-card__dates">
                <span>Erstellt {fmtDate(it.created_at)}</span>
                {it.expires_at ? (
                  <span>· Läuft ab {fmtDate(it.expires_at)}</span>
                ) : (
                  <span>· Unbegrenzt</span>
                )}
              </div>
              <div className="delivery-card__actions">
                <button
                  type="button"
                  className="admin-link admin-link--accent"
                  onClick={() => copyLink(it.slug, it.id)}
                  data-testid={`delivery-copy-${it.id}`}
                >
                  {copiedId === it.id ? "Kopiert ✓" : "Link kopieren"}
                </button>
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="admin-link admin-link--accent"
                >
                  Öffnen ↗
                </a>
                <Link to={`/admin/deliveries/edit/${it.id}`} className="admin-link admin-link--accent">
                  Bearbeiten
                </Link>
                <button
                  type="button"
                  className="admin-link"
                  onClick={() => toggleActive(it)}
                  data-testid={`delivery-toggle-${it.id}`}
                >
                  {it.active ? "Sperren" : "Freischalten"}
                </button>
                <button
                  type="button"
                  className="admin-link admin-link--danger"
                  onClick={() => remove(it)}
                >
                  Löschen
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <Link to="/admin" className="admin-back" style={{ marginTop: 32 }}>
        ← Zurück zur Übersicht
      </Link>
    </div>
  );
}
